import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Chip,
  LinearProgress,
  CircularProgress,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Tooltip,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  AccessTime as AccessTimeIcon,
  DirectionsCar as DirectionsCarIcon,
  Group as GroupIcon,
  Draw as DrawIcon,
  CheckCircle as CheckCircleIcon,
  Assignment as AssignmentIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Warning as WarningIcon,
  Edit as EditIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import SignatureCanvas from 'react-signature-canvas';
import { useSelector } from 'react-redux';
import { rapportiniService, cantieriService, interventiService, usersService } from '../services/api';

const RapportinoModal = ({ open, onClose, intervento, timbrature, presenze, onSuccess }) => {
  const [descrizioneIntervento, setDescrizioneIntervento] = useState('');
  const [materialiUtilizzati, setMaterialiUtilizzati] = useState('');
  const [note, setNote] = useState('');
  const [nomeFirmatario, setNomeFirmatario] = useState('');
  const [responsabileAssente, setResponsabileAssente] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [taskUpdates, setTaskUpdates] = useState({});
  const [searchTask, setSearchTask] = useState('');
  const [allegati, setAllegati] = useState([]);
  const [orePerTecnico, setOrePerTecnico] = useState([]);
  const [calcolo, setCalcolo] = useState(null); // orari + ore calcolate dal server (read-only)
  const sigCanvas = useRef(null);
  const fileInputRef = useRef(null);

  // #2 dal rapportino: editor dei tecnici assegnati (riusa l'endpoint scoped e la sua guardia)
  const { user } = useSelector((state) => state.auth);
  const [tecniciList, setTecniciList] = useState([]);   // elenco completo selezionabile
  const [rosterIds, setRosterIds] = useState([]);       // tecnici assegnati all'intervento (id)
  const [editingTecnici, setEditingTecnici] = useState(false);
  const [tecniciEdit, setTecniciEdit] = useState([]);
  const [tecniciError, setTecniciError] = useState('');
  const [savingTecnici, setSavingTecnici] = useState(false);
  const isAdminUser = ['admin', 'super_admin', 'amministratore'].includes(user?.role);
  const canEditTecnici = !!intervento && (isAdminUser || intervento.capocantiere_id === user?.id);

  // Ore decimali -> "Xh Ym"
  const formatOre = (n) => {
    const v = Number(n) || 0;
    const h = Math.floor(v);
    const m = Math.round((v - h) * 60);
    return m ? `${h}h ${m}m` : `${h}h`;
  };

  // Carica le task del cantiere quando si apre il modal
  useEffect(() => {
    if (open && intervento?.cantiere_id) {
      loadTasks();
    }
  }, [open, intervento]);

  // Ore e orari calcolati automaticamente dal server dalle timbrature (mai a mano, #14/#15)
  useEffect(() => {
    if (open && intervento?.id) {
      rapportiniService.getCalcolo(intervento.id)
        .then((res) => {
          setCalcolo(res.data);
          setOrePerTecnico(res.data.ore_per_tecnico || []);
        })
        .catch(() => setCalcolo(null));
    }
  }, [open, intervento]);

  // #2: pre-riempi la descrizione con la bozza "lavori svolti" scritta durante il lavoro
  useEffect(() => {
    if (open && intervento?.descrizione_rapportino) {
      setDescrizioneIntervento((prev) => prev || intervento.descrizione_rapportino);
    }
  }, [open, intervento]);

  // Carica l'elenco tecnici selezionabili solo se l'utente può modificarli
  useEffect(() => {
    if (open && canEditTecnici && tecniciList.length === 0) {
      usersService.getTecnici()
        .then((res) => setTecniciList(res.data || []))
        .catch(() => {});
    }
  }, [open, canEditTecnici, tecniciList.length]);

  // Roster iniziale dai tecnici dell'intervento
  useEffect(() => {
    if (open && intervento?.tecnici) {
      setRosterIds(intervento.tecnici.map((t) => t.id));
    }
  }, [open, intervento]);

  const loadTasks = async () => {
    if (!intervento?.cantiere_id) return;

    try {
      setLoadingTasks(true);
      const response = await cantieriService.getTasks(intervento.cantiere_id);
      const tasksData = Array.isArray(response.data.tasks) ? response.data.tasks : [];
      setTasks(tasksData);

      // Inizializza taskUpdates con i valori attuali
      const initialUpdates = {};
      tasksData.forEach(task => {
        initialUpdates[task.id] = {
          id: task.id,
          descrizione: task.descrizione,
          quantita: task.quantita,
          quantita_oggi: 0,  // Quantità da svolgere oggi (inizialmente 0)
          quantita_completata: task.quantita_completata || 0,
          completata: task.completata || false,
        };
      });
      setTaskUpdates(initialUpdates);
    } catch (error) {
      console.error('Errore nel caricamento tasks:', error);
      setTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  };

  const handleTaskQuantityChange = (taskId, quantitaOggi) => {
    const value = parseFloat(quantitaOggi) || 0;
    const task = tasks.find(t => t.id === taskId);
    const quantitaOriginale = task?.quantita_completata || 0;
    const nuovoTotale = quantitaOriginale + value;

    setTaskUpdates(prev => ({
      ...prev,
      [taskId]: {
        ...prev[taskId],
        quantita_oggi: value,  // Quantità svolta oggi
        quantita_completata: nuovoTotale,  // Totale che verrà salvato
        completata: task && task.quantita > 0 ? nuovoTotale >= task.quantita : false,
      }
    }));
  };

  // --- Editor tecnici direttamente dal rapportino (punto 2) ---
  const handleStartEditTecnici = () => {
    setTecniciEdit([...rosterIds]);
    setTecniciError('');
    setEditingTecnici(true);
  };

  const handleCancelEditTecnici = () => {
    setEditingTecnici(false);
    setTecniciError('');
  };

  const handleSaveTecnici = async () => {
    if (!intervento?.id) return;
    try {
      setSavingTecnici(true);
      setTecniciError('');
      const res = await interventiService.updateTecnici(intervento.id, { tecnici_ids: tecniciEdit });
      // La risposta (200) contiene l'intervento aggiornato
      const aggiornati = res.data?.tecnici ? res.data.tecnici.map((t) => t.id) : tecniciEdit;
      setRosterIds(aggiornati);
      setEditingTecnici(false);
      // Ricalcola ore/orari dal server: gli operatori mostrati potrebbero cambiare
      try {
        const calc = await rapportiniService.getCalcolo(intervento.id);
        setCalcolo(calc.data);
        setOrePerTecnico(calc.data.ore_per_tecnico || []);
      } catch (_) { /* in caso di errore restano le ore correnti */ }
    } catch (err) {
      setTecniciError(err.response?.data?.message || "Errore nell'aggiornamento dei tecnici");
    } finally {
      setSavingTecnici(false);
    }
  };

  // Gestione allegati
  const handleFileSelect = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const newAllegati = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      // Limita dimensione file a 5MB
      if (file.size > 5 * 1024 * 1024) {
        setError(`Il file ${file.name} supera i 5MB`);
        continue;
      }

      // Converti file in base64
      const reader = new FileReader();
      const base64Promise = new Promise((resolve) => {
        reader.onload = (e) => {
          resolve({
            filename: file.name,
            data_base64: e.target.result,
            content_type: file.type,
            size: file.size,
          });
        };
      });
      reader.readAsDataURL(file);

      const allegato = await base64Promise;
      newAllegati.push(allegato);
    }

    setAllegati(prev => [...prev, ...newAllegati]);

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveAllegato = (index) => {
    setAllegati(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const isImage = (contentType) => {
    return contentType && contentType.startsWith('image/');
  };

  // Calcola dati automatici
  const calcolaDati = () => {
    const dati = {
      ore_lavorate: 0,
      km_percorsi: 0,
      orari: {},
      tecnici_presenti: [],
      tecnici_assenti: [],
    };

    // Estrai orari dalle timbrature
    if (timbrature && timbrature.length > 0) {
      timbrature.forEach(t => {
        const time = new Date(t.timestamp);
        const timeStr = `${time.getHours().toString().padStart(2, '0')}:${time.getMinutes().toString().padStart(2, '0')}`;

        switch (t.tipo) {
          case 'partenza_sede':
            dati.orari.partenza_sede = timeStr;
            break;
          case 'arrivo_cantiere':
            dati.orari.arrivo_cantiere = timeStr;
            dati.orari.inizio_lavoro = timeStr;  // Arrivo cantiere = inizio lavoro
            break;
          case 'inizio_lavoro':
            // Retrocompatibilità: se esiste una vecchia timbratura inizio_lavoro separata, usa quella
            dati.orari.inizio_lavoro = timeStr;
            break;
          case 'fine_lavoro':
            dati.orari.fine_lavoro = timeStr;
            break;
          case 'partenza_cantiere':
            dati.orari.partenza_cantiere = timeStr;
            break;
          case 'arrivo_sede':
            dati.orari.arrivo_sede = timeStr;
            break;
          default:
            break;
        }
      });

      // Calcola ore lavorate
      const inizioLavoro = timbrature.find(t => t.tipo === 'inizio_lavoro') || timbrature.find(t => t.tipo === 'arrivo_cantiere');
      const fineLavoro = timbrature.find(t => t.tipo === 'fine_lavoro');
      if (inizioLavoro && fineLavoro) {
        const inizio = new Date(inizioLavoro.timestamp);
        const fine = new Date(fineLavoro.timestamp);
        const diff = (fine - inizio) / 1000 / 60 / 60; // ore
        dati.ore_lavorate = Math.round(diff * 100) / 100;
      }
    }

    // Calcola km (approssimativo basato su tempo di viaggio)
    if (intervento?.tempo_viaggio_andata) {
      // Stima: 60 km/h di media
      dati.km_percorsi = Math.round((intervento.tempo_viaggio_andata / 60) * 60 * 2);
    }

    // Lista tecnici presenti e assenti
    if (presenze && presenze.length > 0) {
      presenze.forEach(p => {
        if (p.uscita_anticipata) {
          dati.tecnici_assenti.push({
            nome: p.user_nome,
            motivo: `Uscita anticipata: ${p.motivo_uscita_anticipata}`,
            ruolo: p.ruolo
          });
        } else if (p.presente) {
          dati.tecnici_presenti.push({
            nome: p.user_nome,
            ruolo: p.ruolo
          });
        } else {
          dati.tecnici_assenti.push({
            nome: p.user_nome,
            motivo: p.motivo_assenza,
            ruolo: p.ruolo
          });
        }
      });
    }

    return dati;
  };

  const datiCalcolati = calcolaDati();

  const handleClearSignature = () => {
    sigCanvas.current.clear();
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      // Per manutenzioni, richiedi firma e descrizione (#3: messaggi espliciti sul campo mancante)
      if (intervento.tipologia === 'manutenzione') {
        if (!descrizioneIntervento.trim()) {
          setError('Impossibile salvare il rapportino: manca la "Descrizione Evento" (obbligatoria per le manutenzioni).');
          return;
        }
        if (!responsabileAssente) {
          if (sigCanvas.current.isEmpty()) {
            setError('Impossibile salvare il rapportino: manca la firma del cliente (obbligatoria per le manutenzioni). Se il responsabile è assente, spunta "Responsabile assente".');
            return;
          }
          if (!nomeFirmatario.trim()) {
            setError('Impossibile salvare il rapportino: manca il "Nome del Firmatario".');
            return;
          }
        }
      }

      // Prepara le task svolte - include solo task con quantità oggi > 0
      const tasksSvolte = Object.values(taskUpdates).filter(task => {
        // Includi solo task dove è stata inserita una quantità per oggi
        return task.quantita_oggi > 0;
      });

      console.log('DEBUG Frontend - taskUpdates:', taskUpdates);
      console.log('DEBUG Frontend - tasksSvolte filtrate:', tasksSvolte);

      // Prepara dati rapportino
      const rapportinoData = {
        intervento_id: intervento.id,
        descrizione_intervento: descrizioneIntervento || '',  // Stringa vuota invece di null
        materiali_utilizzati: materialiUtilizzati,
        note: note,
        tasks_svolte: tasksSvolte,  // Invia le task modificate
        // Le ore (totali e per tecnico) sono calcolate dal server dalle timbrature: non si inviano.
      };

      console.log('DEBUG Frontend - rapportinoData:', rapportinoData);

      // Aggiungi firma se presente (non in caso di responsabile assente)
      if (intervento.tipologia === 'manutenzione' && !responsabileAssente && sigCanvas.current && !sigCanvas.current.isEmpty()) {
        rapportinoData.firma_cliente = sigCanvas.current.toDataURL('image/png');
        rapportinoData.nome_firmatario = nomeFirmatario;
      }

      // Aggiungi allegati se presenti
      if (allegati.length > 0) {
        rapportinoData.allegati = allegati;
      }

      await rapportiniService.create(rapportinoData);

      onSuccess && onSuccess();
      handleClose();
    } catch (error) {
      console.error('Errore nella creazione del rapportino:', error);
      if (error.isOffline && error.queued) {
        // Salvato nella coda offline: chiudi come un salvataggio riuscito (il toast globale avvisa)
        handleClose();
        return;
      }
      // #3: mostra il MOTIVO reale del fallimento (messaggio del backend) invece di un generico
      // "Errore", così l'utente sa cosa correggere.
      const backendMsg = error.response?.data?.message || error.response?.data?.error;
      setError(
        error.isOffline
          ? error.message
          : (backendMsg
              ? `Impossibile salvare il rapportino: ${backendMsg}`
              : 'Impossibile salvare il rapportino. Controlla i campi obbligatori e riprova. Se il problema persiste, verifica le timbrature dell\'intervento.')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setDescrizioneIntervento('');
    setMaterialiUtilizzati('');
    setNote('');
    setNomeFirmatario('');
    setResponsabileAssente(false);
    setError('');
    setTasks([]);
    setTaskUpdates({});
    setSearchTask('');
    setAllegati([]);
    setOrePerTecnico([]);
    setCalcolo(null);
    setEditingTecnici(false);
    setTecniciEdit([]);
    setTecniciError('');
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
    onClose();
  };

  // Filtra le task in base alla ricerca
  const filteredTasks = tasks.filter(task =>
    task.descrizione.toLowerCase().includes(searchTask.toLowerCase())
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <DescriptionIcon />
          Compila Rapportino di Lavoro
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Informazioni Intervento */}
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            Informazioni Intervento
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Cliente</Typography>
              <Typography variant="body1">{intervento?.cliente}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Cantiere</Typography>
              <Typography variant="body1">{intervento?.cantiere}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Codice</Typography>
              <Typography variant="body1">{intervento?.codice}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Tipologia</Typography>
              <Chip label={intervento?.tipologia} size="small" color="primary" />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Orari e Calcoli */}
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            <AccessTimeIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            Orari e Tempi
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fase</TableCell>
                  <TableCell align="right">Orario</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(datiCalcolati.orari).map(([key, value]) => (
                  <TableRow key={key}>
                    <TableCell>{key.replace(/_/g, ' ').toUpperCase()}</TableCell>
                    <TableCell align="right">{value || '-'}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell><strong>Km Percorsi (stima)</strong></TableCell>
                  <TableCell align="right"><strong>{datiCalcolati.km_percorsi} km</strong></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Team e Ore per Tecnico */}
        <Box mb={3}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
              <GroupIcon sx={{ mr: 1 }} />
              Team — Ore Lavorate
            </Typography>
            {canEditTecnici && !editingTecnici && (
              <Tooltip title="Aggiungi o togli i tecnici assegnati all'intervento">
                <span>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={handleStartEditTecnici}
                    disabled={tecniciList.length === 0}
                  >
                    Modifica tecnici
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>

          {canEditTecnici && (
            <Box sx={{ mb: 2 }}>
              {editingTecnici ? (
                <Box>
                  {tecniciError && (
                    <Alert severity="error" sx={{ mb: 1 }}>{tecniciError}</Alert>
                  )}
                  <Autocomplete
                    multiple
                    options={tecniciList}
                    getOptionLabel={(option) => `${option.nome} ${option.cognome}`}
                    value={tecniciList.filter((t) => tecniciEdit.includes(t.id))}
                    onChange={(event, newValue) => setTecniciEdit(newValue.map((t) => t.id))}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tecnici assegnati"
                        placeholder="Cerca tecnici..."
                        autoFocus
                        fullWidth
                      />
                    )}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Le ore restano calcolate dalle timbrature. Non puoi togliere chi ha già timbrature o presenze su questo intervento.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1, justifyContent: 'flex-end' }}>
                    <Button size="small" onClick={handleCancelEditTecnici} disabled={savingTecnici}>
                      Annulla
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveTecnici}
                      disabled={savingTecnici}
                      sx={{ bgcolor: '#e50000', '&:hover': { bgcolor: '#c50000' } }}
                    >
                      {savingTecnici ? 'Salvataggio…' : 'Salva tecnici'}
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
                    Tecnici assegnati:
                  </Typography>
                  {rosterIds.length > 0 ? (
                    rosterIds.map((tid) => {
                      const t = tecniciList.find((x) => x.id === tid);
                      const label = t
                        ? `${t.nome} ${t.cognome}`
                        : (intervento?.tecnici?.find((x) => x.id === tid)?.nome || `#${tid}`);
                      return <Chip key={tid} label={label} size="small" variant="outlined" />;
                    })
                  ) : (
                    <Typography variant="body2" color="text.secondary">nessun tecnico assegnato</Typography>
                  )}
                </Box>
              )}
            </Box>
          )}

          {orePerTecnico.length > 0 ? (
            <>
              <Alert severity="info" sx={{ mb: 2 }} icon={<AccessTimeIcon />}>
                Le ore sono calcolate automaticamente dalle timbrature (pause e uscite anticipate incluse) e non sono modificabili.
              </Alert>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Operatore</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Ore lavorate</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Note</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orePerTecnico.map((t) => (
                      <TableRow key={t.user_id}>
                        <TableCell>
                          <Chip label={t.nome} color="success" size="small" variant="outlined" />
                        </TableCell>
                        <TableCell align="center">
                          <strong>{formatOre(t.ore)}</strong>
                        </TableCell>
                        <TableCell>
                          {t.uscita_anticipata && (
                            <Typography variant="caption" color="error">
                              Uscita anticipata {t.uscita_anticipata}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {calcolo && (
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Totale intervento</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, color: '#e50000' }}>
                          {formatOre(calcolo.ore_lavorate)}
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              Le ore verranno calcolate automaticamente dalle timbrature.
            </Alert>
          )}

          {datiCalcolati.tecnici_assenti.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>Assenti/Usciti</Typography>
              {datiCalcolati.tecnici_assenti.map((t, idx) => (
                <Chip
                  key={idx}
                  label={`${t.nome}: ${t.motivo}`}
                  color="error"
                  size="small"
                  sx={{ mr: 1, mb: 1 }}
                />
              ))}
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Task Svolte */}
        <Box mb={3}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssignmentIcon />
            Task Svolte Durante l'Intervento
          </Typography>
          {loadingTasks ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : !Array.isArray(tasks) || tasks.length === 0 ? (
            <Alert severity="info">Nessuna task associata a questo cantiere</Alert>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Aggiorna la quantità completata per le task svolte durante questo intervento
              </Alert>

              {/* Campo di ricerca */}
              <Box mb={2}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Cerca task per descrizione..."
                  value={searchTask}
                  onChange={(e) => setSearchTask(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AssignmentIcon sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {filteredTasks.length === 0 ? (
                <Alert severity="warning">Nessuna task trovata con "{searchTask}"</Alert>
              ) : (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {filteredTasks.length} task trovate {searchTask && `su ${tasks.length} totali`}
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Descrizione Task</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>Quantità Totale</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>Già Completata</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>Completata Oggi</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>Progresso</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredTasks.map((task) => {
                          const taskUpdate = taskUpdates[task.id] || {};
                          const quantitaTotale = task.quantita || 0;
                          const quantitaOriginale = task.quantita_completata || 0;
                          const quantitaOggi = taskUpdate.quantita_oggi || 0;
                          const quantitaNuovaTotale = quantitaOriginale + quantitaOggi;
                          const percentuale = quantitaTotale > 0
                            ? Math.round((quantitaNuovaTotale / quantitaTotale) * 100)
                            : 0;
                          const isCompleted = quantitaNuovaTotale >= quantitaTotale;

                          return (
                            <TableRow key={task.id}>
                              <TableCell>
                                <Box display="flex" alignItems="center" gap={1}>
                                  {isCompleted ? (
                                    <CheckCircleIcon sx={{ color: '#4caf50', fontSize: '1.2rem' }} />
                                  ) : (
                                    <Box sx={{ width: 20, height: 20, borderRadius: '50%', border: '2px solid #9e9e9e' }} />
                                  )}
                                  <Typography variant="body2">{task.descrizione}</Typography>
                                </Box>
                              </TableCell>
                              <TableCell align="center">
                                <Chip label={quantitaTotale} size="small" variant="outlined" />
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={quantitaOriginale}
                                  size="small"
                                  color={quantitaOriginale > 0 ? 'primary' : 'default'}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <TextField
                                  type="number"
                                  size="small"
                                  value={quantitaOggi}
                                  onChange={(e) => handleTaskQuantityChange(task.id, e.target.value)}
                                  inputProps={{
                                    min: 0,
                                    max: quantitaTotale - quantitaOriginale,
                                    step: 0.5,
                                    style: { textAlign: 'center' }
                                  }}
                                  sx={{
                                    width: 100,
                                    '& input': {
                                      fontWeight: quantitaOggi > 0 ? 700 : 400,
                                      color: quantitaOggi > 0 ? '#2196f3' : 'inherit'
                                    }
                                  }}
                                  variant="outlined"
                                  placeholder="0"
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Box sx={{ width: '100%', minWidth: 100 }}>
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <Box sx={{ flex: 1 }}>
                                      <LinearProgress
                                        variant="determinate"
                                        value={percentuale}
                                        sx={{
                                          height: 8,
                                          borderRadius: 1,
                                          backgroundColor: '#e0e0e0',
                                          '& .MuiLinearProgress-bar': {
                                            backgroundColor: isCompleted ? '#4caf50' : '#2196f3',
                                          },
                                        }}
                                      />
                                    </Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 40 }}>
                                      {percentuale}%
                                    </Typography>
                                  </Box>
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </>
          )}
        </Box>

        {/* Descrizione */}
        <Box mb={3}>
          <TextField
            label={intervento?.tipologia === 'manutenzione' ? "Descrizione Evento *" : "Descrizione Lavori (opzionale)"}
            multiline
            rows={3}
            fullWidth
            required={intervento?.tipologia === 'manutenzione'}
            value={descrizioneIntervento}
            onChange={(e) => setDescrizioneIntervento(e.target.value)}
            placeholder={intervento?.tipologia === 'manutenzione'
              ? "Descrivi l'evento o il problema riscontrato (obbligatorio per manutenzioni)..."
              : "Eventuali dettagli aggiuntivi sul lavoro svolto..."}
            helperText={intervento?.tipologia === 'manutenzione' ? "Campo obbligatorio per interventi di manutenzione" : ""}
          />
        </Box>

        {/* Materiali */}
        <Box mb={3}>
          <TextField
            label="Materiali Utilizzati"
            multiline
            rows={3}
            fullWidth
            value={materialiUtilizzati}
            onChange={(e) => setMaterialiUtilizzati(e.target.value)}
            placeholder="Elenca i materiali utilizzati durante l'intervento..."
          />
        </Box>

        {/* Note */}
        <Box mb={3}>
          <TextField
            label="Note Aggiuntive"
            multiline
            rows={2}
            fullWidth
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Eventuali note o osservazioni..."
          />
        </Box>

        {/* Firma (solo per manutenzioni) */}
        {intervento?.tipologia === 'manutenzione' && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box mb={3}>
              <Typography variant="h6" gutterBottom>
                <DrawIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                Firma Cliente
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={responsabileAssente}
                    onChange={(e) => setResponsabileAssente(e.target.checked)}
                    color="warning"
                  />
                }
                label="Responsabile assente — firma posticipata"
                sx={{ mb: 2, display: 'block' }}
              />

              {responsabileAssente && (
                <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
                  Il rapportino verrà salvato senza firma. Sarà necessario raccogliere la firma in un secondo momento.
                </Alert>
              )}

              {!responsabileAssente && (
                <>
                  <TextField
                    label="Nome del Firmatario *"
                    fullWidth
                    value={nomeFirmatario}
                    onChange={(e) => setNomeFirmatario(e.target.value)}
                    sx={{ mb: 2 }}
                    placeholder="Nome e cognome di chi firma"
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Firma qui sotto con il dito: la tastiera si chiude automaticamente.
                  </Typography>
                  <Box
                    onPointerDown={() => { if (document.activeElement && document.activeElement.blur) document.activeElement.blur(); }}
                    sx={{
                      border: '2px solid #ddd',
                      borderRadius: 1,
                      p: 1,
                      backgroundColor: 'white',
                      overflowX: 'auto',
                    }}
                  >
                    <SignatureCanvas
                      ref={sigCanvas}
                      penColor="black"
                      onBegin={() => { if (document.activeElement && document.activeElement.blur) document.activeElement.blur(); }}
                      canvasProps={{
                        width: 500,
                        height: 200,
                        className: 'sigCanvas',
                        style: { touchAction: 'none' },
                      }}
                    />
                  </Box>
                  <Button
                    onClick={handleClearSignature}
                    size="small"
                    sx={{ mt: 1 }}
                  >
                    Pulisci Firma
                  </Button>
                </>
              )}
            </Box>
          </>
        )}

        {/* Allegati (file e foto) */}
        <Divider sx={{ my: 2 }} />
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            <AttachFileIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            Allegati (Foto/Documenti)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Puoi allegare foto, PDF o altri documenti (max 5MB per file)
          </Typography>

          {/* Input file nascosto */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />

          {/* Pulsante upload */}
          <Button
            variant="outlined"
            startIcon={<AttachFileIcon />}
            onClick={() => fileInputRef.current?.click()}
            sx={{ mb: 2 }}
          >
            Aggiungi File/Foto
          </Button>

          {/* Lista allegati */}
          {allegati.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                File caricati: {allegati.length}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {allegati.map((allegato, index) => (
                  <Paper
                    key={index}
                    elevation={1}
                    sx={{
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                    }}
                  >
                    {/* Icona o preview */}
                    {isImage(allegato.content_type) ? (
                      <Box
                        component="img"
                        src={allegato.data_base64}
                        alt={allegato.filename}
                        sx={{
                          width: 60,
                          height: 60,
                          objectFit: 'cover',
                          borderRadius: 1,
                          border: '1px solid #ddd',
                        }}
                      />
                    ) : (
                      <InsertDriveFileIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
                    )}

                    {/* Info file */}
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" fontWeight="bold">
                        {allegato.filename}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatFileSize(allegato.size)}
                      </Typography>
                    </Box>

                    {/* Pulsante elimina */}
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleRemoveAllegato(index)}
                    >
                      Rimuovi
                    </Button>
                  </Paper>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Annulla
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading}
        >
          {loading ? 'Salvataggio...' : 'Salva Rapportino'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RapportinoModal;